import { Link, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { VisualTrailer } from '../components/trailer/VisualTrailer'
import { publishLoadStatusChanged, subscribeToLoadStatusChanges } from '../lib/load-status-sync'

export default function DriverLoadPlanMobilePage() {
  const { loadId } = useParams<{ loadId: string }>()
  const [draft, setDraft] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [deckStates, setDeckStates] = useState<Record<string, { height: number; angle: number }>>({})
  const [savedDeckStates, setSavedDeckStates] = useState<Record<string, { height: number; angle: number }>>({})
  const [rampStates, setRampStates] = useState<Record<string, 'stored' | 'deployed' | 'raised' | 'lowered'>>({})
  const [savedRampStates, setSavedRampStates] = useState<Record<string, string>>({})

  const query = useQuery({
    queryKey: ['driver-load-plan-mobile', loadId],
    queryFn: async () => (await api.loads.getDriverTrailerPlanView(loadId!)).data,
    enabled: !!loadId,
    refetchInterval: 15000,
  })

  useEffect(() => loadId ? subscribeToLoadStatusChanges(loadId, () => { void query.refetch() }) : undefined, [loadId])
  useEffect(() => { setDraft(null); setFeedback('') }, [loadId])
  useEffect(() => { const nextDeckStates = query.data?.planner?.deckStates ?? {}; const nextRampStates = query.data?.planner?.rampStates ?? {}; setDeckStates(nextDeckStates); setSavedDeckStates(nextDeckStates); setRampStates(nextRampStates); setSavedRampStates(nextRampStates) }, [query.data?.planner?.updatedAt])

  if (query.isLoading) {
    return <div className="p-4 text-sm text-gray-500">Loading mobile load plan...</div>
  }

  if (!query.data) {
    return <div className="p-4 text-sm text-red-600">Unable to load driver load plan.</div>
  }

  const plan = query.data
  const terminal = [plan.status, plan.operationalStatus].some(status => ['DELIVERED', 'COMPLETED', 'CANCELLED', 'PAID'].includes(String(status).toUpperCase()))
  const canEdit = !terminal && Boolean(plan.planner?.driverEditPermission?.unlocked)
  const visibleVehicles = draft?.loadId === loadId ? draft.vehicles : plan.vehicles
  const currentVehicles = draft?.loadId === loadId ? draft.vehicles : plan.vehicles
  const vehicleDirty = draft?.loadId === loadId
  const mechanicalDirty = JSON.stringify(deckStates) !== JSON.stringify(savedDeckStates) || JSON.stringify(rampStates) !== JSON.stringify(savedRampStates)
  const planDirty = vehicleDirty || mechanicalDirty
  async function save() {
    if (!canEdit || !planDirty || !loadId) return
    setSaving(true)
    try {
      await api.loads.saveTrailerPlan(loadId, {
        ...{ expectedUpdatedAt: draft?.updatedAt ?? plan.planner?.updatedAt ?? null },
        vehicles: currentVehicles.map((vehicle: any) => ({ vehicleId: vehicle.vehicleId, trailerPosition: vehicle.trailerPosition,
          pickupSequence: vehicle.pickupSequence, deliverySequence: vehicle.deliverySequence })),
        deckStates, rampStates,
      })
      setSavedDeckStates(deckStates)
      setSavedRampStates(rampStates)
      await query.refetch()
      setDraft(null)
      setFeedback('Plan saved.')
      publishLoadStatusChanged(loadId)
    } catch (error: any) { setFeedback(error?.response?.data?.error ?? 'Unable to save. Your draft is still here.') }
    finally { setSaving(false) }
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Driver Load Plan</h1>
          <p className="text-xs text-gray-500 mt-1">{plan.loadNumber || plan.loadId}</p>
        </div>
        <Link to="/driver/jobs" className="border rounded px-3 py-2 text-xs">My Jobs</Link>
      </div>

      <div className="rounded-xl border bg-white p-3 space-y-2 text-sm">
        <div><span className="text-gray-500">Status:</span> {plan.status || '—'}</div>
        <div><span className="text-gray-500">Origin:</span> {plan.route?.origin || '—'}</div>
        <div><span className="text-gray-500">Destination:</span> {plan.route?.destination || '—'}</div>
        <div><span className="text-gray-500">Payload:</span> {plan.payload?.projectedTotalWeight?.toLocaleString?.() || plan.payload?.projectedTotalWeight || 0} lb</div>
        <div><span className="text-gray-500">Capacity:</span> {plan.payload?.slotCapacityWeight?.toLocaleString?.() || plan.payload?.slotCapacityWeight || 0} lb</div>
        <div><span className="text-gray-500">Utilization:</span> {plan.payload?.utilizationPercent != null ? `${plan.payload.utilizationPercent}%` : '—'}</div>
      </div>

      <div className="rounded-xl border bg-white p-3 space-y-2">
        <h2 className="text-sm font-semibold text-gray-900">Load / Unload Steps</h2>
        <VisualTrailer positions={plan.layoutDescriptor.positions}
          slotToVehicle={new Map((visibleVehicles || []).filter((vehicle: any) => vehicle.trailerPosition).map((vehicle: any) => [vehicle.trailerPosition, vehicle]))}
          equipmentLabel={[plan.assignedEquipment?.trailerModel, plan.assignedEquipment?.trailerNumber].filter(Boolean).join(' · ') || plan.assignedEquipment?.equipmentType || null}
          geometry={plan.assignedEquipment?.geometry}
          deckStates={deckStates} rampStates={rampStates}
          onDeckStateChange={(id, state) => setDeckStates(previous => ({ ...previous, [id]: state }))}
          onRampStateChange={(id, state) => setRampStates(previous => ({ ...previous, [id]: state }))}
          onMove={(vehicleId, slot) => setDraft((previous: any) => ({ loadId,
            updatedAt: previous ? previous.updatedAt : plan.planner?.updatedAt ?? null,
            vehicles: (previous?.vehicles ?? plan.vehicles).map((vehicle: any) => vehicle.vehicleId === vehicleId ? { ...vehicle, trailerPosition: slot } : vehicle),
          }))}
          readOnly={!canEdit || saving} />
        {terminal ? <p>Transport closed — historical plan.</p> : canEdit ? <div className="flex gap-3">
          <button className="min-h-[44px] rounded bg-blue-700 px-4 text-white disabled:opacity-50" disabled={!planDirty || saving} onClick={save}>Save plan</button>
          <button className="min-h-[44px] rounded border px-4" disabled={saving} onClick={() => { setDraft(null); void query.refetch() }}>Discard draft / reload</button>
        </div> : <p className="text-sm">View only. Your dispatcher must unlock plan editing.</p>}
        <p role="status">{feedback}</p>
        <div className="grid grid-cols-1 gap-2 text-xs">
          <div className="rounded-lg border bg-gray-50 p-2">
            <div className="font-semibold text-gray-700 mb-1">Load Order</div>
            <pre className="whitespace-pre-wrap text-[11px] text-gray-700">{plan.driverNotes?.loadOrder || 'No load order available.'}</pre>
          </div>
          <div className="rounded-lg border bg-gray-50 p-2">
            <div className="font-semibold text-gray-700 mb-1">Unload Order</div>
            <pre className="whitespace-pre-wrap text-[11px] text-gray-700">{plan.driverNotes?.unloadOrder || 'No unload order available.'}</pre>
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-3 space-y-2">
        <h2 className="text-sm font-semibold text-gray-900">Assigned Units</h2>
        <div className="space-y-2">
          {(plan.vehicles || []).map((vehicle: any) => (
            <div key={vehicle.vehicleId} className="rounded-lg border p-2 text-xs space-y-1">
              <div className="font-semibold text-gray-800">{vehicle.vin || vehicle.vehicleId}</div>
              <div className="text-gray-600">Slot: {vehicle.trailerPosition || 'unassigned'}</div>
              <div className="text-gray-600">Pickup #{vehicle.pickupSequence || '—'} · Delivery #{vehicle.deliverySequence || '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {(plan.warnings || []).length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-1 text-xs text-amber-900">
          <h2 className="font-semibold">Warnings</h2>
          {(plan.warnings || []).map((warning: string) => (
            <div key={warning} className="rounded border border-amber-200 bg-white px-2 py-1">{warning}</div>
          ))}
        </div>
      )}
    </div>
  )
}
